import React, { useEffect } from 'react';
import { HashRouter as Router, Route, Routes, Navigate } from 'react-router-dom';
import { App as CapacitorApp } from '@capacitor/app';
import Login from './Login';  // Import the Login component
import SignInLink from './SignInLink';  // Magic-link landing page (/sign-in/:token)
import DeepLinkHandler from './DeepLinkHandler';  // Routes OS deep links into /sign-in/:token
import OpenInApp from './OpenInApp';  // Emailed https hand-off into the desktop app (/open-app/:token)
import SetupGate from './SetupGate';  // First-login security setup (/setup)
import { SelectedProvider } from './SelectedContext';
import { AuthProvider } from './AuthContext';
import ProtectedRoute from './ProtectedRoute';
import TableOrder from './pages/TableOrder';
import Tables from './pages/Tables';
import Orders from './pages/Orders';
import Customers from './pages/Customers';
import Profile from './pages/Profile';
import EditCustomer from './pages/EditCustomer';
import OrderView from './pages/OrderView';
import Platform from './utils/platform';
import StaffLock from './pages/StaffLock';
import CloseShift from './components/CloseShift';
import CloseUserShift from './pages/CloseUserShift';
import PrinterSettings from './pages/PrinterSettings';
import { NetworkStatusProvider } from './contexts/NetworkStatusContext';
import { startSyncQueueListener } from './services/SyncQueueProcessor';
import ReferenceDataService from './services/ReferenceDataService';
import UpdateBanner from './components/UpdateBanner';

function App(): React.JSX.Element {

  useEffect(() => {
    // Initialize mobile-specific features
    if (Platform.isMobile()) {
      // Handle Android back button
      CapacitorApp.addListener('backButton', ({ canGoBack }) => {
        if (!canGoBack) {
          CapacitorApp.exitApp();
        } else {
          window.history.back();
        }
      });

      // Handle app state changes
      CapacitorApp.addListener('appStateChange', ({ isActive }) => {
        console.log('App state changed. Is active:', isActive);
      });

      // Log platform info
      console.log('Running on:', Platform.getPlatform());
    }

    // Start offline sync queue processor
    startSyncQueueListener();

    // Cache reference data for offline use
    const propertyId = localStorage.getItem('propertyId');
    if (propertyId) {
      ReferenceDataService.syncAll(propertyId);
    }

    return () => {
      // Cleanup listeners
      if (Platform.isMobile()) {
        CapacitorApp.removeAllListeners();
      }
    };
  }, []);

  return (
    <NetworkStatusProvider>
    <AuthProvider>
      <SelectedProvider>
        <Router>
          <DeepLinkHandler />
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/sign-in/:token" element={<SignInLink />} />
            <Route path="/open-app/:token" element={<OpenInApp />} />
            <Route path="/setup" element={<SetupGate />} />
            <Route path="/" element={<Navigate to="/tables" replace />} />
            <Route path="/tables/:tableId" element={
              <ProtectedRoute requiredPermission="pos-app.module.access">
                <TableOrder />
              </ProtectedRoute>
            } />
            <Route path="/orders/:orderId" element={
              <ProtectedRoute requiredPermission="pos-app.order.view">
                <OrderView />
              </ProtectedRoute>
            } />
            <Route path="/tables" element={
              <ProtectedRoute requiredPermission="pos-app.module.access">
                <Tables />
              </ProtectedRoute>
            } />
            <Route path="/orders" element={
              <ProtectedRoute requiredPermission="pos-app.order.view">
                <Orders />
              </ProtectedRoute>
            } />
            <Route path="/customers" element={
              <ProtectedRoute>
                <Customers />
              </ProtectedRoute>
            } />
            <Route path="/customers/:customerId" element={
              <ProtectedRoute>
                <EditCustomer />
              </ProtectedRoute>
            } />
            <Route path="/profile" element={
              <ProtectedRoute>
                <Profile />
              </ProtectedRoute>
            } />
            {/* The PIN screen. Inside the property session — hence Protected —
              * but reachable with nobody signed in, which is its whole job. */}
            <Route path="/lock" element={
              <ProtectedRoute allowLocked>
                <StaffLock />
              </ProtectedRoute>
            } />
            <Route path="/waiter-selection" element={<Navigate to="/lock" replace />} />
            <Route path="/close-shift" element={
              <ProtectedRoute requiredPermission="pos-app.shift.end-day">
                <CloseShift />
              </ProtectedRoute>
            } />
            <Route path="/close-user-shift" element={
              <ProtectedRoute requiredPermission="pos-app.shift.close">
                <CloseUserShift />
              </ProtectedRoute>
            } />
            <Route path="/printer-settings" element={
              <ProtectedRoute>
                <PrinterSettings />
              </ProtectedRoute>
            } />



          </Routes>
        </Router>
      </SelectedProvider>
    </AuthProvider>
    <UpdateBanner />
    </NetworkStatusProvider>
  );
}

export default App;
